/**
 * OmegaRefuseListPage — `/omega/refuse-list`.
 *
 * Read-only render of the 8 prohibited classes that the engine
 * enforces at admission (Protocol Clause 5). Each entry is permanent
 * for the lifetime of this engine (Constitution C-10: refusals
 * cannot be deleted). All 8 classes ship in the active state at
 * Spark; pause / unpause is a quorum-gated operation that the
 * dashboard observes — it does not initiate.
 */

import { Link, Navigate } from 'react-router-dom';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { cn } from '@/utils/cn';

interface RefuseEntry {
  clause: string;
  title: string;
  description: string;
  status: 'active' | 'inactive';
}

const REFUSE_LIST: RefuseEntry[] = [
  {
    clause: 'Clause 5 · Class 1',
    title: 'Classified data',
    description:
      'No ingestion or production of material classified by any government’s national-security regime — Israeli SECRET and above, US SECRET / TOP SECRET, EU CONFIDENTIEL UE / SECRET UE, etc. Classified workflows live in dedicated, air-gapped engines, not OMEGA.',
    status: 'active',
  },
  {
    clause: 'Clause 5 · Class 2',
    title: 'Weapons systems',
    description:
      'No manifests, contracts, or test fixtures for autonomous lethal decision systems, targeting systems, or kinetic-effect dispatch. Refuse-at-admission per Protocol Clause 5 prohibited class 1.',
    status: 'active',
  },
  {
    clause: 'Clause 5 · Class 3',
    title: 'Mass-surveillance authoring',
    description:
      'No facial-recognition deployment products at scale, no social-scoring systems whose outputs affect basic rights, no products whose declared purpose includes identifying specific individuals across video / location / financial data without their consent.',
    status: 'active',
  },
  {
    clause: 'Clause 5 · Class 4',
    title: 'Deepfake / identity-theft tooling',
    description:
      'No products whose declared behaviour synthesises a third party’s voice, image, or text identity for misrepresentation. The video adapter’s deepfake-shield starter is the opposite — proving authenticity, not generating fakes.',
    status: 'active',
  },
  {
    clause: 'Clause 5 · Class 5',
    title: 'Coordinated political manipulation',
    description:
      'No products that target voters with algorithmically-generated persuasion, that obscure the source of political messaging, or that inflate engagement metrics on political content.',
    status: 'active',
  },
  {
    clause: 'Clause 5 · Class 6',
    title: 'CSAM detection without victim safeguards',
    description:
      'No products that scan citizens’ personal storage for CSAM patterns absent the safeguards described in Refuse List class 6 — judicial warrant, victim-identification protocol, prosecutorial chain-of-custody. The engineering case for client-side scanning is not a counter-argument.',
    status: 'active',
  },
  {
    clause: 'Clause 5 · Class 7',
    title: 'Personal-data trafficking',
    description:
      'No products whose declared purpose is the bulk sale, transfer, or exposure of personal data outside the legal basis declared in the originating consent.',
    status: 'active',
  },
  {
    clause: 'Clause 5 · Class 8',
    title: 'Bypass of safety-critical certifications',
    description:
      'No products whose declared behaviour replaces or short-circuits a safety-critical certification (FAA airworthiness, FDA medical-device clearance, IEC 60601 alarm, automotive ASIL). The medical adapter produces compliance-workflow artifacts; it does not produce the certification itself.',
    status: 'active',
  },
];

export function OmegaRefuseListPage() {
  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }

  const activeCount = REFUSE_LIST.filter((r) => r.status === 'active').length;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb label="Refuse List" />

      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Static reference — not live engine data. These entries are placeholders until
        the engine publishes them on the OMEGA log.
      </div>

      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          Refuse List
        </h1>
        <p className="text-sm text-ink-muted">
          {REFUSE_LIST.length} prohibited classes the engine enforces at
          admission. {activeCount} active. All entries are permanent for
          the lifetime of this engine (Constitution C-10).
        </p>
      </header>

      <section className="rounded-xl border border-border bg-surface p-6">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-lg font-semibold text-ink">
            Protocol Clause 5 entries
          </h2>
          <span className="font-mono text-xs text-ink-subtle">
            {REFUSE_LIST.length} entries
          </span>
        </div>
        <ol className="mt-5 flex flex-col gap-3">
          {REFUSE_LIST.map((entry) => (
            <li
              key={entry.clause}
              className="flex flex-col gap-2 rounded-lg border border-border bg-canvas px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs uppercase tracking-wide text-ink-subtle">
                    {entry.clause}
                  </span>
                  <span className="text-sm font-medium text-ink">
                    {entry.title}
                  </span>
                </div>
                <StatusBadge status={entry.status} />
              </div>
              <p className="text-sm text-ink-muted">{entry.description}</p>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: 'active' | 'inactive' }) {
  const isActive = status === 'active';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5',
        'text-[10px] font-semibold uppercase tracking-wide',
        isActive
          ? 'border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300'
          : 'border-border bg-canvas text-ink-subtle',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          isActive ? 'bg-fuchsia-400' : 'bg-ink-subtle',
        )}
      />
      {status}
    </span>
  );
}

function Breadcrumb({ label }: { label: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
      <Link
        to="/omega"
        className="text-ink-muted transition-colors hover:text-ink"
      >
        &larr; OMEGA
      </Link>
      <span className="text-ink-subtle">/</span>
      <span className="text-ink">{label}</span>
    </nav>
  );
}
